<template>
  <div class="friend-stat-box">
    <!-- LEFT SIDE -->
    <div class="left-side">
      <!-- Top (start Date Summary) -->
      <div class="date-box start-date">
        <div class="header">
          <span class="title">{{ selectedData }}</span>
          <span class="date">{{ formattedStartDate }}</span>
        </div>
        <div class="value">{{ startValue }}</div>
      </div>

      <!-- Bottom (end Date Summary) -->
      <div class="date-box end-date">
        <div class="header">
          <span class="title">{{ selectedData }}</span>
          <span class="date">{{ formattedEndDate }}</span>
        </div>
        <div class="value">{{ endValue }}</div>
      </div>
    </div>

    <!-- RIGHT SIDE -->
    <div class="right-side">
      <div class="bars-container">
        <!-- Start Bar -->
        <div class="bar-group">
          <div
            class="bar start-bar"
            :style="{
              height: startBarHeight + '%',
              backgroundColor: brandGrey,
            }"
          ></div>
          <div class="bar-value">{{ startValue }}</div>
          <div class="bar-date">{{ formattedStartDate }}</div>
        </div>

        <!-- End Bar -->
        <div class="bar-group end-bar-group">
          <div
            class="bar end-bar"
            :style="{
              height: endBarHeight + '%',
              backgroundColor: endBarColor,
            }"
          ></div>
          <div class="bar-value">{{ endValue }}</div>
          <div class="bar-date">{{ formattedEndDate }}</div>
          <div
            class="percent-change"
            :style="{ color: percentChangeColor, top: percentChangeTop }"
            :key="percentChangeString"
          >
            ({{ percentChangeString }})
          </div>
        </div>

        <!-- Cumulative Bar (placeholder) -->
        <div class="bar-group cumulative-bar-group">
          <div
            class="bar cumulative-bar"
            :style="{
              height: cumulativeBarHeight + '%',
              backgroundColor: cumulativeBarColor,
            }"
          ></div>
          <div class="bar-value">{{ cumulativeValue }}</div>
          <div class="bar-date">{{ $t('userAdmin.statsCumulative') }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue'

  const CUMULATIVE_VALUE_OFFSET = 1
  const BAR_GROUP_HEIGHT_PX = 200
  const PERCENT_LABEL_Y_OFFSET_PX = 8

  export default Vue.extend({
    name: 'FriendStatBox',
    props: {
      selectedData: {
        type: String,
        required: true,
      },
      startDate: {
        type: String,
        required: true,
      },
      endDate: {
        type: String,
        required: true,
      },
      startValue: {
        type: Number,
        required: true,
      },
      endValue: {
        type: Number,
        required: true,
      },
      cumulativeValue: {
        type: Number,
        required: false,
        default: 0,
      },
    },
    computed: {
      formattedStartDate(): string {
        return this.formatDate(this.startDate)
      },
      formattedEndDate(): string {
        return this.formatDate(this.endDate)
      },
      percentChangeColor(): string {
        if (this.endValue > this.startValue) return this.brandGreen
        if (this.endValue < this.startValue) return this.brandRed
        return this.brandGrey
      },
      percentChange(): number {
        if (this.startValue === 0) return 0
        return ((this.endValue - this.startValue) / this.startValue) * 100
      },
      percentChangeString(): string {
        const sign = this.percentChange > 0 ? '+' : ''
        return `${sign}${this.percentChange.toFixed(0)}%`
      },
      percentChangeTop(): string {
        const barHeightPx = (this.endBarHeight / 100) * BAR_GROUP_HEIGHT_PX
        const barTopPx = BAR_GROUP_HEIGHT_PX - barHeightPx
        return `${Math.max(0, barTopPx + PERCENT_LABEL_Y_OFFSET_PX)}px`
      },
      endBarColor(): string {
        if (this.endValue > this.startValue) return this.brandGreen
        if (this.endValue < this.startValue) return this.brandRed
        return this.brandGrey
      },
      cumulativeBarColor(): string {
        return '#4f7ecb'
      },
      startBarHeight(): number {
        const maxVal = Math.max(this.startValue, this.endValue, this.cumulativeValue, 1)
        return (this.startValue / maxVal) * 100
      },
      endBarHeight(): number {
        const maxVal = Math.max(this.startValue, this.endValue, this.cumulativeValue, 1)
        return (this.endValue / maxVal) * 100
      },
      cumulativeBarHeight(): number {
        const maxVal = Math.max(this.startValue, this.endValue, this.cumulativeValue, 1)
        return (this.cumulativeValue / maxVal) * 100
      },
      brandGreen(): string {
        return (
          getComputedStyle(document.documentElement).getPropertyValue('--brand-green') || '#00b050'
        )
      },
      brandRed(): string {
        return (
          getComputedStyle(document.documentElement).getPropertyValue('--brand-red') || '#e30613'
        )
      },
      brandGrey(): string {
        return (
          getComputedStyle(document.documentElement).getPropertyValue('--brand-grey13') || '#888'
        )
      },
    },
    methods: {
      formatDate(dateString: string): string {
        const d = new Date(dateString)
        if (isNaN(d.getTime())) return dateString
        return d.toLocaleDateString('fi-FI', {
          day: 'numeric',
          month: 'numeric',
          year: 'numeric',
        })
      },
    },
  })
</script>

<style lang="scss" scoped>
  @import '~@/assets/scss/_variables.scss';

  .friend-stat-box {
    display: flex;
    border-radius: 16px;
    background-color: $brand-white;
    padding: 1rem;
    width: 90%;
    font-family: 'Signa-Regular', sans-serif;
    color: $brand-black;

    .left-side {
      flex: 1 1 35%;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      border-right: 2px solid $brand-grey5;
      padding-right: 1rem;

      .date-box {
        flex: 1;
        display: flex;
        flex-direction: column;

        .header {
          position: relative; // allows absolute positioning of the date
          text-align: center; // center the title
          margin-bottom: 1.5rem;
          padding-top: 1rem;

          .title {
            display: block;
            font-family: 'Signa-Bold', sans-serif;
            font-size: 1.1rem;
            color: $brand-black;
          }

          .date {
            right: 0; // far right of the box
            top: 0; // slightly above or aligned with title
            font-size: 0.9rem;
            color: $brand-grey12;
            font-weight: bold;
          }
        }

        .value {
          font-size: 2rem;
          font-family: 'Signa-Bold', sans-serif;
          text-align: center;
          color: $brand-black;
        }
      }
      .start-date {
        border-bottom: 2px solid $brand-grey5;
      }
    }

    .right-side {
      flex: 1 1 65%;
      display: flex;
      align-items: center;
      justify-content: center;
      padding-left: 1rem;

      .bars-container {
        display: flex;
        align-items: flex-end;
        justify-content: center;
        gap: 2rem;
        width: 100%;
        height: 300px;
        position: relative;

        .bar-group {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-end;
          position: relative;
          height: 200px;

          .bar {
            width: 40px;
            border-radius: 8px 8px 0 0;
            transition: all 0.3s ease;
          }

          .bar-value {
            margin-top: -1.5rem;
            font-weight: bold;
            font-size: 1rem;
          }

          .bar-date {
            margin-top: 0.5rem;
            font-size: 0.9rem;
            color: $brand-grey12;
            display: inline-block;
            width: 90px; // fixed width for consistent layout
            text-align: center;
          }

          &.end-bar-group {
            .percent-change {
              position: absolute;
              right: -40px;
              font-size: 0.95rem;
              white-space: nowrap;
              transition:
                color 0.3s ease,
                opacity 0.3s ease;
              opacity: 1;
            }
          }

          &.cumulative-bar-group {
            margin-left: 1.75rem;
          }
        }
      }
    }
  }
</style>
